import { createFileRoute } from "@tanstack/react-router";
import ServiceVoiceAI from "@/pages/ServiceVoiceAI";
import {
  buildPageHead,
  breadcrumbSchema,
  serviceSchema,
  faqPageSchema,
} from "@/lib/seo";

const faqs = [
  {
    question: "What does a Voice AI architect actually do?",
    answer:
      "A Voice AI architect designs and builds the entire conversational system: Vapi + Twilio telephony, LLM prompt engineering, objection handling logic, inbound/outbound routing, instant CRM sync, and call analytics. They engineer the infrastructure, not just configure it.",
  },
  {
    question: "Can a Voice AI agent really handle sales objections?",
    answer:
      "Yes, when engineered correctly. I map behavioral triggers to dynamic LLM responses so the agent adapts its objection handling based on what the prospect says. The key is prompt engineering: context-aware personas, fallback scripts, and qualification logic built into the conversation flow.",
  },
  {
    question: "How much does a Voice AI architect cost?",
    answer:
      "Engagements are scoped per project or retainer. A senior Voice AI architect delivers the full conversational architecture at a fraction of a US agency rate. Book a free voice AI audit call to receive a custom scope and quote for your Vapi automation needs.",
  },
  {
    question: "What platforms do you integrate with Voice AI?",
    answer:
      "Vapi and Twilio for the voice layer, with CRM sync to GoHighLevel, HubSpot, Close CRM, and any platform with webhook support. Call transcripts, summaries, and extracted data push to the contact record the moment a call ends.",
  },
  {
    question: "How long does it take to deploy a Voice AI agent?",
    answer:
      "A production-ready Vapi agent with objection handling, CRM sync, and call routing takes 1–2 weeks. Simple inbound qualification agents can ship in 3–5 days. Complex multi-agent systems with n8n orchestration take 3–4 weeks.",
  },
  {
    question: "Can you fix a voice agent another freelancer built?",
    answer:
      "Yes. I inherit and rebuild broken voice agents with a documented audit first — mapping every prompt, call flow, and webhook before any changes, so nothing breaks during the transition.",
  },
  {
    question: "Can Voice AI replace my receptionist or SDR?",
    answer:
      "A Voice AI agent handles inbound qualification, outbound calling, appointment booking, and FAQ answering 24/7 at a fraction of the cost. It escalates to humans only when needed, reducing manual admin by up to 80% and accelerating lead response to under 30 seconds.",
  },
  {
    question: "How do we get started without disrupting live calls?",
    answer:
      "Every engagement begins with an audit and staging blueprint. We test all prompts, call flows, and CRM sync in a sandbox environment before rollout — ensuring your active call operations run 100% uninterrupted.",
  },
];

export const Route = createFileRoute("/voice-ai-vapi-automation")({
  head: () =>
    buildPageHead({
      title: "Sub-300ms Voice AI & Vapi Automation Expert",
      description:
        "Hire a Voice AI and Vapi integration specialist in the Philippines. I build LLM-integrated objection handling workflows, inbound/outbound routing, and automated conversational agents with instant CRM sync.",
      path: "/voice-ai-vapi-automation",
      keywords:
        "voice AI automation, Vapi automation, agentic voice AI, AI voice agent, LLM voice integration, conversational AI, voice AI specialist Philippines, Vapi developer, AI calling agent, Twilio voice AI",
      schema: [
        serviceSchema({
          name: "Agentic Voice AI & Vapi Automation",
          description:
            "LLM-integrated voice AI agents with objection handling, CRM sync, and automated conversational workflows via Vapi.",
          path: "/voice-ai-vapi-automation",
          serviceType: [
            "Voice AI Implementation",
            "Vapi Integration",
            "Conversational Agent Engineering",
            "AI Calling Automation",
          ],
        }),
        breadcrumbSchema([
          { name: "Home", path: "" },
          {
            name: "Voice AI & Vapi Automation",
            path: "/voice-ai-vapi-automation",
          },
        ]),
        faqPageSchema(faqs),
      ],
    }),
  component: ServiceVoiceAI,
});
